import React, { useState, useCallback, useRef, useEffect } from 'react'
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
  Paper,
  Popper,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import TableChartIcon from '@mui/icons-material/TableChart'
import StarIcon from '@mui/icons-material/Star'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import DescriptionIcon from '@mui/icons-material/Description'
import AbcIcon from '@mui/icons-material/Abc'
import SummarizeIcon from '@mui/icons-material/Summarize'
import { canAccessSkySocialMedia } from '../utils/skyNav'
import CampaignIcon from '@mui/icons-material/Campaign'
import FolderIcon from '@mui/icons-material/Folder'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import DashboardIcon from '@mui/icons-material/Dashboard'
import Chip from '@mui/material/Chip'
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone'
import useAccount from '../hooks/useAccount'
import useRouter from '../hooks/useRouter'
import useRecentTables from '../hooks/useRecentTables'
import axios from 'axios'
import { searchJobData, SearchResult } from '../services/admin/searchApi'
import { API_BASE_URL } from '../constants/system'
import PageHeader from '../components/system/PageHeader'
import PageAgentButton from '../agent/components/PageAgentButton'
import PageAgentDrawer from '../agent/components/PageAgentDrawer'
import { useVoiceRegistration } from '../voice/useVoiceRegistration'
import { buildVoiceTools } from '../voice/buildVoiceTools'
import { useDisplayNames } from '../utils/displayNames'
import { timeOfDayGreeting } from '../utils/greeting'

const Dashboard: React.FC = () => {
  const account = useAccount()
  const router = useRouter()
  const customer = account.customer?.name || ''
  const job = account.job?.name || ''
  const recentTables = useRecentTables(customer, job, account.toc)
  const greeting = timeOfDayGreeting()

  // Search
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<SearchResult[]>([])
  const [searching, setSearching] = useState(false)
  // The Home helper (Douglas plan T15).
  const [helperOpen, setHelperOpen] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const searchFieldRef = useRef<HTMLDivElement>(null)

  type TimelineItem =
    { type: 'notification', content: string, link?: { label: string, page: string, params?: Record<string, string> }, timestamp: number }

  const [timelineItems, setTimelineItems] = useState<TimelineItem[]>([])

  const sessionId = account.user?.session_info?.sessionId || ''

  // Load timeline: recent tables + system notifications
  useEffect(() => {
    const items: TimelineItem[] = recentTables.tables.slice(0, 5).map(t => ({
      type: 'notification' as const,
      content: `Opened table ${t.name}`,
      link: { label: t.name, page: 'tables', params: { table: t.path.replace(/\.cbt$/, '') } },
      timestamp: t.timestamp,
    }))

    // Fetch system notifications
    if (sessionId && customer && job) {
      axios.get(`${API_BASE_URL}/notifications`, {
        params: { customer, job, limit: 20 },
        headers: { Authorization: `Bearer ${sessionId}` },
      }).then(res => {
        const notifications = (res.data || []) as Array<{
          message: string
          notification_type: string
          created_at: number
        }>
        for (const n of notifications) {
          items.push({
            type: 'notification',
            content: n.message,
            timestamp: n.created_at * 1000,
          })
        }
        setTimelineItems(items.sort((a, b) => b.timestamp - a.timestamp))
      }).catch(() => {
        setTimelineItems(items.sort((a, b) => b.timestamp - a.timestamp))
      })
    } else {
      setTimelineItems(items.sort((a, b) => b.timestamp - a.timestamp))
    }
  }, [customer, job, sessionId])
  const userPermissions = account.user?.domain_permissions
  const isManager = account.user?.role_permissions?.isManager

  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim() || !customer || !job || !sessionId) return
    setSearching(true)
    setHasSearched(true)
    try {
      const response = await searchJobData(sessionId, {
        query: searchQuery.trim(),
        customer,
        job,
        limit: 10,
      })
      setSearchResults((response.results || []).filter(r => r.content_type !== 'variable'))
    } catch {
      setSearchResults([])
    } finally {
      setSearching(false)
    }
  }, [searchQuery, customer, job, sessionId])

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch()
    if (e.key === 'Escape') handleClearSearch()
  }

  const handleClearSearch = () => {
    setSearchQuery('')
    setSearchResults([])
    setHasSearched(false)
  }

  const handleResultClick = (result: SearchResult) => {
    handleClearSearch()
    if (result.content_type === 'table') {
      account.openPage('tables', { table: result.path.replace(/\.cbt$/, '') })
    } else if (result.content_type === 'variable') {
      router.navigate('variables', { customer, job, highlightVariable: result.name })
    }
  }

  const getResultIcon = (contentType: string) => {
    switch (contentType) {
      case 'table': return <TableChartIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
      case 'variable': return <AbcIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
      default: return <DescriptionIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
    }
  }

  // Quick links
  const quickLinks = [
    { label: 'Tables', icon: <TableChartIcon />, onClick: () => account.openPage('tables'), visible: true },
    { label: 'Variables', icon: <AbcIcon />, onClick: () => account.openPage('variables'), visible: true },
    { label: 'Files', icon: <FolderIcon />, onClick: () => account.openPage('files'), visible: true },
    { label: 'Executive Reports', icon: <SummarizeIcon />, onClick: () => account.openPage('executive'), visible: !!userPermissions?.canAccessExecutiveReports },
    // T6.1 — see skyNav.ts: customer AND job, and the chooser rather than the
    // customer/job dashboard Track 6 retires.
    { label: 'Social Media', icon: <CampaignIcon />, onClick: () => account.openPage('sky-social-media'), visible: canAccessSkySocialMedia(userPermissions, account.customer?.name, account.job?.name) },
    { label: 'DataViz', icon: <DashboardIcon />, onClick: () => router.navigate('dashboards', { customer }), visible: !!userPermissions?.canAccessDashboards },
    { label: 'AutoCoder', icon: <SmartToyIcon />, onClick: () => account.openPage('autocoder'), visible: !!isManager }, // Manager-only since 30 Sep 2026 (access audit T11)
    { label: 'Agent', icon: <SmartToyIcon />, onClick: () => router.navigate('agent', { customer }), visible: !!userPermissions?.canAccessAgent },
  ].filter(l => l.visible)

  const showSearchResults = hasSearched || searching

  // Douglas reads this back aloud, so it carries display names rather than

  // the codes in the URL (#1347 item 1).

  const names = useDisplayNames(customer)


  useVoiceRegistration({
    pageKey: `dashboard:${customer}:${job}`,
    pageContext: () => `The project dashboard / search for ${names.customer}/${names.job(job)}. You can type into the search box, run it, and clear it.`,
    uiTools: buildVoiceTools({
      fields: [
        { name: 'set_dashboard_search', label: 'the dashboard search box', set: (v: string) => setSearchQuery(v) },
      ],
      actions: [
        { name: 'search_tables', label: 'run the search', run: () => handleSearch() },
        { name: 'clear_search', label: 'clear the search', run: () => handleClearSearch() },
      ],
    }),
  })

  if (!account.user || !customer) {
    return null
  }

  return (
    <Box sx={{ height: 'calc(100vh - 64px)', display: 'flex', overflow: 'hidden' }}>
      {/* Left column — Quick Links + Recent Tables (full height) */}
      <Box sx={{
        width: 380,
        flexShrink: 0,
        borderRight: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'background.paper',
      }}>
        {/* Quick Links */}
        <Box sx={{ pt: 2.5, pb: 0.5 }}>
          <Typography variant="subtitle2" sx={{
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            fontSize: '0.75rem',
            px: 3,
            mb: 0.5,
          }}>
            Quick Links
          </Typography>
          {quickLinks.map(link => (
            <Box
              key={link.label}
              onClick={link.onClick}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 3,
                py: 1.25,
                cursor: 'pointer',
                '&:hover': { backgroundColor: 'action.hover' },
              }}
            >
              <Box sx={{ color: 'text.secondary', display: 'flex', '& .MuiSvgIcon-root': { fontSize: 16 } }}>
                {link.icon}
              </Box>
              <Typography variant="body2">
                {link.label}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', mx: 3, mt: 1 }} />

        {/* Search */}
        <Box sx={{ px: 3, pt: 2, pb: 0.5 }}>
          <Typography variant="subtitle2" sx={{
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            fontSize: '0.75rem',
            mb: 1,
          }}>
            Search
          </Typography>
        </Box>
        <Box ref={searchFieldRef} sx={{ px: 3, pb: 1 }}>
          <TextField
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            placeholder="Search tables..."
            fullWidth
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {searching && <CircularProgress size={16} sx={{ mr: 0.5 }} />}
                  {searchQuery && !searching && (
                    <IconButton size="small" onClick={handleClearSearch} sx={{ p: 0.25 }}>
                      <ClearIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', mx: 3, mt: 1 }} />

        {/* Recent Tables */}
        <Box sx={{ px: 3, pt: 2, pb: 1 }}>
          <Typography variant="subtitle2" sx={{
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            fontSize: '0.75rem',
          }}>
            Recent Tables
          </Typography>
        </Box>
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {recentTables.tables.length > 0 ? (
            recentTables.tables.slice(0, 15).map(t => (
              <Box
                key={t.path}
                onClick={() => account.openPage('tables', { table: t.path.replace(/\.cbt$/, '') })}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 3,
                  py: 1.25,
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: 'action.hover' },
                }}
              >
                <TableChartIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {t.name}
                </Typography>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation()
                    recentTables.toggleStar(t.path)
                  }}
                  sx={{ p: 0.25, color: t.starred ? 'secondary.main' : 'text.secondary', opacity: t.starred ? 1 : 0.3, '&:hover': { opacity: 1 } }}
                >
                  {t.starred ? <StarIcon sx={{ fontSize: 14 }} /> : <StarBorderIcon sx={{ fontSize: 14 }} />}
                </IconButton>
              </Box>
            ))
          ) : (
            <Box sx={{ px: 3, py: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Tables you open will appear here
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Right side — Header + AI Chat */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Header with search */}
        <PageHeader
          title={greeting}
          subtitle={job ? `${names.customer} / ${names.job(job)}` : names.customer}
          actions={
            <PageAgentButton
              pageKey="dashboard"
              customer={customer}
              open={helperOpen}
              onClick={() => setHelperOpen(o => !o)}
            />
          }
        />

      {/* Search results dropdown */}
      <Popper
        open={showSearchResults}
        anchorEl={searchFieldRef.current}
        placement="bottom-start"
        sx={{ zIndex: 1300, width: 340 }}
      >
        <Paper elevation={8} sx={{ mt: 0.5, maxHeight: 400, overflow: 'auto', borderRadius: '6px' }}>
          {searching && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={20} />
            </Box>
          )}
          {!searching && searchResults.length === 0 && hasSearched && (
            <Box sx={{ px: 3, py: 2.5 }}>
              <Typography variant="body2" color="text.secondary">
                No results found
              </Typography>
            </Box>
          )}
          {!searching && searchResults.length > 0 && (
            <>
              <Typography variant="caption" sx={{ px: 2, pt: 1.5, pb: 0.5, color: 'text.secondary', display: 'block' }}>
                {searchResults.length} result{searchResults.length !== 1 ? 's' : ''}
              </Typography>
              {searchResults.map((result, i) => (
                <Box
                  key={result.id || i}
                  onClick={() => handleResultClick(result)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 2,
                    py: 1,
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'action.hover' },
                  }}
                >
                  {getResultIcon(result.content_type)}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {result.display_name || result.name}
                    </Typography>
                    {result.description && (
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                        {result.description}
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
                    {result.content_type}
                  </Typography>
                </Box>
              ))}
            </>
          )}
        </Paper>
      </Popper>

        {/* Timeline */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', backgroundColor: 'background.paper' }}>
          <Box sx={{ px: 3, pt: 3, pb: 1.5 }}>
            <Typography variant="subtitle2" sx={{
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              fontSize: '0.75rem',
            }}>
              Timeline
            </Typography>
          </Box>

          {/* Timeline items */}
          <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 1 }}>
            {timelineItems.length === 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <Typography variant="body2" color="text.secondary">
                  No recent activity
                </Typography>
              </Box>
            )}
            {timelineItems.sort((a, b) => a.timestamp - b.timestamp).map((item, i) => {
              return (
                <Box key={i} sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  py: 1.5,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  borderLeft: '2px solid #00B2FF',
                  pl: 1.5,
                }}>
                  <NotificationsNoneIcon sx={{ fontSize: 16, color: 'text.secondary', mt: 0.25 }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                      {item.content}
                      {item.link && (
                        <Chip
                          label={item.link.label}
                          size="small"
                          onClick={() => account.openPage(item.link!.page, item.link!.params)}
                          sx={{
                            cursor: 'pointer',
                            fontSize: '0.75rem',
                            height: 20,
                            bgcolor: '#E3F2FD',
                            color: '#424242',
                            fontWeight: 500,
                            border: '1px solid #BBDEFB',
                            '& .MuiChip-label': { px: 1 },
                            '&:hover': { bgcolor: '#BBDEFB', color: '#212121', borderColor: '#90CAF9' },
                          }}
                        />
                      )}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {new Date(item.timestamp).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                    </Typography>
                  </Box>
                </Box>
              )
            })}
          </Box>

        </Box>
      </Box>

      <PageAgentDrawer
        pageKey="dashboard"
        customer={customer}
        job={job}
        open={helperOpen}
        onClose={() => setHelperOpen(false)}
      />
    </Box>
  )
}

export default Dashboard
